import { useEffect, useState, type MouseEvent } from "react";
import { Button, Checkbox, DatePicker, Input, Select } from "antd";
import dayjs from "dayjs";
import type { FilterDefinition, FilterValues } from "../../types/chartFilters";
import styles from "./ChartFilterBar.module.css";

const { RangePicker } = DatePicker;

export type FilterOnChange = (
  key: string,
  value: string | string[] | [number, number] | undefined
) => void;

interface ChartFilterBarProps {
  filters: FilterDefinition[];
  values: FilterValues;
  onChange: FilterOnChange;
  disabled?: boolean;
}

type MultiSelectFilterDefinition = Extract<FilterDefinition, { type: "multiSelect" }>;

interface BatchMultiSelectProps {
  filter: MultiSelectFilterDefinition;
  selected: string[];
  disabled?: boolean;
  onApply: (value: string[]) => void;
}

const BatchMultiSelect = ({ filter, selected, disabled, onApply }: BatchMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(selected);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!open) {
      setDraft(selected);
      setSearchTerm("");
    }
  }, [open, selected]);

  const selectedDraft = new Set(draft);
  const normalizedSearch = searchTerm.trim().toLocaleLowerCase();
  const filteredOptions = filter.options.filter((option) => {
    if (!normalizedSearch) return true;
    return option.label.toLocaleLowerCase().includes(normalizedSearch);
  });

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setDraft(selected);
        setSearchTerm("");
    }
  };

  const toggleOption = (value: string) => {
    setDraft((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const keepDropdownOpen = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
  };

  return (
    <Select
      key={filter.key}
      mode="multiple"
      showSearch={false}
      disabled={disabled}
      open={open}
      value={draft}
      options={filter.options}
      placeholder={filter.placeholder ?? filter.label}
      onChange={(nextValues) => {
        const next = nextValues as string[];
        setDraft(next);
        onApply(next);
      }}
      onDropdownVisibleChange={handleOpenChange}
      maxTagCount={3}
      maxTagPlaceholder={() => "..."}
      className={styles.select}
      allowClear
      dropdownRender={() => (
        <div className={styles.batchDropdown} onMouseDown={keepDropdownOpen}>
          <div className={styles.batchToolbar}>
            <div className={styles.batchSearch}>
              <Input
                allowClear
                value={searchTerm}
                placeholder="Cerca opcions..."
                onChange={(event) => setSearchTerm(event.target.value)}
                onMouseDown={keepDropdownOpen}
              />
            </div>
            <div className={styles.batchQuickActions}>
              <button
                type="button"
                className={styles.batchActionBtn}
                onMouseDown={keepDropdownOpen}
                onClick={() => {
                  const visibleValues = filteredOptions.map((option) => option.value);
                  const merged = Array.from(new Set([...draft, ...visibleValues]));
                  setDraft(merged);
                }}
              >
                Seleccionar tot
              </button>
              <button
                type="button"
                className={styles.batchActionBtn}
                onMouseDown={keepDropdownOpen}
                onClick={() => setDraft([])}
              >
                Netejar
              </button>
            </div>
          </div>
          <div className={styles.batchOptions}>
            {filteredOptions.map((option) => {
              const checked = selectedDraft.has(option.value);

              return (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.batchOption} ${checked ? styles.batchOptionActive : ""}`}
                  onClick={() => toggleOption(option.value)}
                >
                  <Checkbox checked={checked} tabIndex={-1} />
                  <span>{option.label}</span>
                </button>
              );
            })}
            {filteredOptions.length === 0 && (
              <div className={styles.batchEmpty}>No hi ha resultats</div>
            )}
          </div>
          <div className={styles.batchActions}>
            <Button
              size="small"
              onMouseDown={keepDropdownOpen}
              onClick={() => {
                setDraft(selected);
                setOpen(false);
              }}
            >
              Cancel·lar
            </Button>
            <Button
              type="primary"
              size="small"
              onMouseDown={keepDropdownOpen}
              onClick={() => {
                onApply(draft);
                setOpen(false);
              }}
            >
              Aplicar
            </Button>
          </div>
        </div>
      )}
    />
  );
};

const ChartFilterBar = ({ filters, values, onChange, disabled }: ChartFilterBarProps) => {
  return (
    <div className={`${styles.container} ${disabled ? styles.disabled : ""}`}>
      {disabled && <div className={styles.disabledOverlay} />}
      <div className={styles.selects}>
        {filters.map((filter) => {
          if (filter.type === "yearRange") {
            const rangeVal = values[filter.key] as [number, number] | undefined;
            return (
              <RangePicker
                key={filter.key}
                picker="year"
                disabled={disabled}
                allowEmpty={[true, true]}
                value={
                  rangeVal
                    ? [dayjs(String(rangeVal[0])), dayjs(String(rangeVal[1]))]
                    : null
                }
                placeholder={[`${filter.minYear}`, `${filter.maxYear}`]}
                minDate={dayjs(String(filter.minYear))}
                maxDate={dayjs(String(filter.maxYear))}
                className={styles.rangePicker}
                onChange={(dates) => {
                  if (dates?.[0] && dates?.[1]) {
                    onChange(filter.key, [dates[0].year(), dates[1].year()]);
                  } else {
                    onChange(filter.key, undefined);
                  }
                }}
              />
            );
          }

          if (filter.type === "multiSelect") {
            const selected = (values[filter.key] as string[]) ?? [];
            return (
              <BatchMultiSelect
                key={filter.key}
                filter={filter}
                selected={selected}
                disabled={disabled}
                onApply={(newValue) => onChange(filter.key, newValue)}
              />
            );
          }

          if (filter.type === "select") {
            const selectVal = values[filter.key] as string | undefined;
            return (
              <Select
                key={filter.key}
                disabled={disabled}
                value={selectVal ?? null}
                options={filter.options}
                placeholder={filter.label}
                onChange={(v: string) => onChange(filter.key, v)}
                className={styles.select}
              />
            );
          }

          return null;
        })}
      </div>
    </div>
  );
};

export default ChartFilterBar;
